import { useCallback, useState } from 'react';

import { useToast } from '@vimeo/bokeh';
import { CircleCheckFilled, CircleExclamationFilled } from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';
import { Reason, REASON_UNSELECTED } from '@vimeo/report-modal';

import { usePostReport } from '../../hooks/use-post-report';

export interface UseReportVideoProps {
  clipId: string;
  /** Fired after a report succeeds (in addition to the success toast). */
  onSuccess?: () => void;
  /** Fired after a report fails (in addition to the failure toast). */
  onFailure?: () => void;
}

/**
 * Shared data wiring for the video report flow — reason state, the post
 * mutation, and success/failure toasts — returned as primitives so each shell
 * (`ReportVideoModal`, `ReportVideoEmbed`) composes its own submit semantics.
 *
 * `postReport` resolves even when the API fails (`usePostVideoReport` swallows
 * errors into reactive state), so success-only actions must use `onSuccess`,
 * not the `await`.
 */
export function useReportVideo({
  clipId,
  onSuccess: onSuccessExternal,
  onFailure: onFailureExternal,
}: UseReportVideoProps) {
  const [reportReason, setReportReason] = useState<Reason>(REASON_UNSELECTED);
  const reportSuccess = translate('Video reported');
  const reportFailure = translate('Video could not be reported');
  const toast = useToast();

  const onFailure = useCallback(() => {
    toast({
      title: reportFailure,
      status: 'error',
      icon: <CircleExclamationFilled />,
    });
    onFailureExternal?.();
  }, [reportFailure, toast, onFailureExternal]);

  const onSuccess = useCallback(() => {
    toast({
      title: reportSuccess,
      status: 'success',
      icon: <CircleCheckFilled />,
    });
    onSuccessExternal?.();
  }, [reportSuccess, toast, onSuccessExternal]);

  const { isPosting, postReport } = usePostReport({ clipId, onFailure, onSuccess });

  const resetReason = useCallback(() => setReportReason(REASON_UNSELECTED), []);

  return { reportReason, setReportReason, resetReason, isPosting, postReport };
}
